import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  LabelValue,
  LabelValueSource,
  WbsLabelValueSearchResult
} from 'graphql/types';

import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { DependantLabels } from '../schema';
import { usePCPTAutoUpdateHandlers } from '../../hooks';

export const useClientOnSelect = (
  onSelect: (value: LabelValue | null) => void,
  dependentLabels: DependantLabels
): ((value?: WbsLabelValueSearchResult | null) => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const {
    project: autoUpdateProject,
    task: autoUpdateTask
  } = usePCPTAutoUpdateHandlers({
    dependentLabels
  });

  const onClientSelect = useCallback(
    async (value?: WbsLabelValueSearchResult | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.displayText
      };

      const { client, project, task, billing } = dependentLabels;

      const isClientDifferent = client?.label.value?.idValue !== value?.id;
      if (isClientDifferent) {
        setValue(
          `${timesheetLabelsFormField}.${project.index}.label`,
          {
            ...project.label,
            value: null,
            valueSource: isSmartSuggestionEnabled
              ? LabelValueSource.Suggestion
              : LabelValueSource.User
          },
          { shouldValidate: true, shouldDirty: true }
        );

        setValue(
          `${timesheetLabelsFormField}.${task.index}.label`,
          {
            ...task.label,
            value: null,
            valueSource: isSmartSuggestionEnabled
              ? LabelValueSource.Suggestion
              : LabelValueSource.User
          },
          { shouldValidate: true, shouldDirty: true }
        );

        if (billing && billing.label) {
          setValue(
            `${timesheetLabelsFormField}.${billing.index}.label`,
            {
              ...billing.label,
              value: null,
              valueSource: isSmartSuggestionEnabled
                ? LabelValueSource.Suggestion
                : LabelValueSource.User
            },
            { shouldValidate: true, shouldDirty: true }
          );
        }

        if (value) {
          const projectResult = await autoUpdateProject({ client: value?.id });

          if (projectResult) {
            await autoUpdateTask({
              client: value?.id,
              project: projectResult.id
            });
          }
        }
      }
      onSelect(value ? transformedValue : null);
    },
    [
      onSelect,
      setValue,
      dependentLabels,
      autoUpdateProject,
      autoUpdateTask,
      isSmartSuggestionEnabled,
      timesheetLabelsFormField
    ]
  );

  return onClientSelect;
};

export default useClientOnSelect;
